'use client'

import { useEffect } from 'react'
import { captureError } from '@platform/observezen-react'

/**
 * Last-resort boundary: fires only when the ROOT layout itself throws, which
 * means Next has discarded that layout and must render <html>/<body> here.
 *
 * Deliberately styled inline rather than with Tailwind or zendesign. The root
 * layout is what imports globals.css and the font variables, so by the time
 * this renders there is no guarantee any stylesheet or component CSS loaded —
 * a token-based class here would render unstyled exactly when the user most
 * needs a legible page. Colours are the brand literals (Orange 300 #EF9846 /
 * ink #1D1916) for the same reason.
 *
 * PARTIAL REPORTING, knowingly. `initSentry` is called only by SentryProvider,
 * which lives in the root layout — the very layout whose failure triggers this
 * boundary. So a crash during a client-side navigation after boot IS captured
 * (Sentry initialised on first mount), while a cold-load root-layout crash is
 * NOT: `captureException` on an uninitialised client is a silent no-op. Do not
 * read the call below as full coverage. Closing the gap means initialising
 * Sentry outside the React tree (instrumentation-client.ts), which is its own
 * ticket. Ref: ASC-7832, PR #1400 review.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  useEffect(() => {
    captureError(error)
  }, [error])

  return (
    <html lang="en">
      <body
        style={{
          margin: 0,
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '16px',
          padding: '24px',
          background: '#1D1916',
          color: '#FFFFFF',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          textAlign: 'center',
        }}
      >
        <h1 style={{ margin: 0, fontSize: '20px', fontWeight: 600 }}>
          Scorpio couldn&apos;t start
        </h1>
        <p style={{ margin: 0, fontSize: '14px', color: '#B9B2AA', maxWidth: '42ch' }}>
          An unexpected error stopped the application from loading. Reloading usually clears it.
        </p>
        {error.digest ? (
          <code style={{ fontSize: '12px', color: '#8A8279' }}>Support ID: {error.digest}</code>
        ) : null}
        <button
          type="button"
          onClick={reset}
          style={{
            marginTop: '8px',
            padding: '9px 18px',
            border: 'none',
            borderRadius: '6px',
            background: '#EF9846',
            color: '#1D1916',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Reload
        </button>
      </body>
    </html>
  )
}
